<script lang="ts">
  import { cubicOut } from "svelte/easing";
  import { onMount } from "svelte";
  import { tweened } from "svelte/motion";
  import type { BridgeStation as Station } from "../../shared/corridor";
  import type { PublicBridge, PublicSnapshot } from "../../shared/contracts";
  import { bridgeCopy, bridgePositionCopy, formatEtTime, freshnessLabel } from "../format";
  import BasculeGlyph from "./BasculeGlyph.svelte";

  export let station: Station;
  export let bridge: PublicBridge;
  export let x: number;
  export let y: number;
  export let variant = 0;
  export let focal = false;
  export let labelX = 0;
  export let labelY = 48;
  export let labelSide: -1 | 1 = 1;
  export let dataMode: PublicSnapshot["dataMode"] = "live";

  const leafAngle = tweened(0, { duration: 720, easing: cubicOut });
  const focalBase = {
    x: -182.5,
    y: -148.5,
    width: 300,
    height: 225,
  } as const;
  const focalLeaf = {
    x: -70.08774373259052,
    y: -148.5,
    width: 152.0891364902507,
    height: 225,
    pivotX: 45.5,
    pivotY: 0,
  } as const;

  let reduceMotion = false;
  let supportsFailed = false;
  let leafFailed = false;

  onMount(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => (reduceMotion = query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  });

  $: trafficStatus = bridgeCopy[bridge.state];
  $: positionStatus = bridgePositionCopy[bridge.state];
  $: confirmed = bridge.lastConfirmedAt
    ? formatEtTime(bridge.lastConfirmedAt)
    : "Not confirmed";
  $: labelAbove = labelY < 0;
  $: labelLeader = labelAbove
    ? `M${labelSide * 27} -10L${labelSide * 36} -23L${labelX + labelSide * 19} ${labelY + 11}`
    : `M${labelSide * 27} 10L${labelSide * 36} 23L${labelX + labelSide * 19} ${labelY - 11}`;
  $: leafAngle.set(bridge.state === "up" ? 58 : 0, {
    duration: reduceMotion ? 0 : 720,
  });
</script>

<g
  class:station--up={bridge.state === "up"}
  class:station--unknown={bridge.state === "unknown"}
  class:station--brickell={focal}
  class="station"
  transform={`translate(${x} ${y})`}
  data-bridge-id={station.id}
>
  {#if focal}
    <circle class="station__focus-field" r="136" />
    <circle class="station__focus-ring" r="126" />
    <text class="station__hero-name" y="-143" text-anchor="middle">BRICKELL</text>
    <text class="station__hero-subtitle" y="-123" text-anchor="middle">AVENUE BRIDGE</text>
  {/if}

  <g
    class="bridge-art"
    class:bridge-art--focal={focal}
    class:bridge-art--compact={!focal}
    data-opening-direction="outward"
    aria-hidden="true"
  >
    {#if focal && !supportsFailed && !leafFailed}
      <defs>
        <clipPath id="brickell-base-clip" clipPathUnits="userSpaceOnUse">
          <rect x="-110" y="-84" width="230" height="150" />
        </clipPath>
      </defs>
      <g class="bridge-art__leaf bridge-art__leaf--left" transform="scale(-1 1)">
        <g
          class="bridge-art__moving"
          data-pivot={`-${focalLeaf.pivotX},${focalLeaf.pivotY}`}
          transform={`rotate(${$leafAngle} ${focalLeaf.pivotX} ${focalLeaf.pivotY})`}
        >
          <image
            href="/assets/bridges/bascule-leaf.webp"
            x={focalLeaf.x}
            y={focalLeaf.y}
            width={focalLeaf.width}
            height={focalLeaf.height}
            preserveAspectRatio="none"
            onerror={() => (leafFailed = true)}
          />
        </g>
      </g>
      <g class="bridge-art__leaf bridge-art__leaf--right">
        <g
          class="bridge-art__moving"
          data-pivot={`${focalLeaf.pivotX},${focalLeaf.pivotY}`}
          transform={`rotate(${$leafAngle} ${focalLeaf.pivotX} ${focalLeaf.pivotY})`}
        >
          <image
            href="/assets/bridges/bascule-leaf.webp"
            x={focalLeaf.x}
            y={focalLeaf.y}
            width={focalLeaf.width}
            height={focalLeaf.height}
            preserveAspectRatio="none"
            onerror={() => (leafFailed = true)}
          />
        </g>
      </g>
      <g
        class="bridge-art__fixed"
        data-control-house-side="left"
        transform="scale(-1 1)"
      >
        <image
          class="bridge-art__supports"
          href="/assets/bridges/bascule-base.webp"
          x={focalBase.x}
          y={focalBase.y}
          width={focalBase.width}
          height={focalBase.height}
          preserveAspectRatio="none"
          clip-path="url(#brickell-base-clip)"
          onerror={() => (supportsFailed = true)}
        />
      </g>
    {:else if focal}
      <BasculeGlyph angle={$leafAngle} scale={2.65} variant={0} />
    {:else}
      <BasculeGlyph angle={$leafAngle} scale={0.72} {variant} />
    {/if}
  </g>

  {#if focal}
    <g class="station__hero-board" transform="translate(0 144)">
      <rect x="-96" y="-25" width="192" height="50" />
      <text class="station__hero-state" y="-4" text-anchor="middle">{trafficStatus.label} · {trafficStatus.consequence}</text>
      <text class="station__hero-freshness" y="14" text-anchor="middle">{freshnessLabel(bridge.freshness, dataMode)} · {confirmed}</text>
    </g>
  {:else}
    <path class="station__label-leader" d={labelLeader} aria-hidden="true" />
    <g class="station__label" transform={`translate(${labelX} ${labelY})`}>
      <text class="station__name" text-anchor="middle">{station.shortName}</text>
      <text class="station__state" y="17" text-anchor="middle">{positionStatus}</text>
      <text class="station__freshness" y="34" text-anchor="middle">{freshnessLabel(bridge.freshness, dataMode)} · {confirmed}</text>
    </g>
  {/if}
</g>
